// Confirmação de assinatura do delegation log (Parte 14.2 "mostrar o que se assina"; REQ-AUTHZ-008): o PI assina ("Delego as tarefas listadas") e a pessoa delegada
// aceita ("Aceito as tarefas delegadas"). O diálogo mostra a delegação, o significado FIXADO pelo servidor e o hash do conteúdo que o usuário viu ("assina o que viu":
// se o conteúdo mudou, o servidor responde 409). O ato exige step-up e é irreversível e auditado: depois do redirect ao provedor a ação NÃO se repete sozinha — a chave
// é por ação E por delegação, a carga é nula e o diálogo, ao reabrir, avisa que a sessão já foi reautenticada para o usuário confirmar de novo.
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Textarea,
} from '@giyus/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useStepUpAction } from '../../../../lib/auth/useStepUp';
import type { Delegation } from '../../api/delegations';
import { Field } from '../../components/Fields';
import { DelegationFailure } from './DelegationFailure';
import { DelegationSummary } from './DelegationSummary';

export type SignatureMode = 'pi-sign' | 'accept';

export interface SignatureConfirmDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly delegation: Delegation;
  readonly mode: SignatureMode;
  readonly pending: boolean;
  readonly error: unknown;
  /** Chama a mutação com o hash que o usuário viu; fecha o diálogo quem a chama quando conclui. */
  readonly onConfirm: (input: { contentHash: string; comment: string | null }) => Promise<void>;
  readonly taskNames?: ReadonlyMap<string, string> | undefined;
}

export function SignatureConfirmDialog(props: SignatureConfirmDialogProps) {
  const { t } = useTranslation('common');
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent
        closeLabel={t('a11y.closeDialog')}
        className="max-h-[90vh] overflow-y-auto"
        data-testid={`${props.mode}-dialog`}
      >
        {props.open && <Body {...props} />}
      </DialogContent>
    </Dialog>
  );
}

function Body({
  onOpenChange,
  delegation,
  mode,
  pending,
  error,
  onConfirm,
  taskNames,
}: SignatureConfirmDialogProps) {
  const { t } = useTranslation(['authz', 'common']);
  // Congela o que o usuário viu: uma releitura em segundo plano não troca o hash por baixo da assinatura.
  const [seen] = useState(delegation);
  const [comment, setComment] = useState('');
  const meaning = mode === 'pi-sign' ? 'Delegate' : 'Accept';
  const scope = mode === 'pi-sign' ? 'sign' : 'accept';

  const action = useStepUpAction<{ readonly comment: string | null }>({
    key: `authz-delegation-${mode}-${seen.id}`,
    run: async (input) => {
      await onConfirm({ contentHash: seen.contentHash, comment: input.comment });
    },
    // Ato irreversível: nada é guardado para repetir sozinho após o redirect do step-up (o usuário confirma de novo).
    toPayload: () => null,
    fromPayload: () => null,
  });

  return (
    <form
      className="flex flex-col gap-4"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        const text = comment.trim();
        action.execute({ comment: text === '' ? null : text }).catch(() => {
          // O erro fica no diálogo (DelegationFailure).
        });
      }}
    >
      <DialogHeader>
        <DialogTitle>{t(`authz:delegations.${scope}.title`)}</DialogTitle>
        <DialogDescription>{t(`authz:delegations.${scope}.description`)}</DialogDescription>
      </DialogHeader>
      <DelegationSummary delegation={seen} showHash />
      <p
        className="rounded-card border bg-muted/40 p-3 text-body font-medium"
        data-testid="signature-meaning"
      >
        <span className="block text-caption font-normal text-muted-foreground">
          {t('authz:delegations.signature.meaning')}
        </span>
        {t(`common:components.signature.meanings.${meaning}`)}
      </p>
      <Field id={`${mode}-comment`} label={t('authz:delegations.signature.comment')}>
        <Textarea
          id={`${mode}-comment`}
          rows={2}
          maxLength={1000}
          value={comment}
          onChange={(event) => {
            setComment(event.target.value);
          }}
        />
      </Field>
      {action.resumedWithoutPayload && (
        <p role="status" className="text-body text-muted-foreground" data-testid={`${mode}-repeat`}>
          {t(`authz:delegations.${scope}.repeat`)}
        </p>
      )}
      <DelegationFailure error={error} taskNames={taskNames} />
      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            onOpenChange(false);
          }}
        >
          {t('common:actions.cancel')}
        </Button>
        <Button type="submit" disabled={pending} data-testid={`${mode}-confirm`}>
          {t(`authz:delegations.${scope}.confirm`)}
        </Button>
      </DialogFooter>
    </form>
  );
}
